import { create } from 'zustand';

/**
 * The current agent's effective customer-chat visibility scope, sourced from
 * `GET /me/permissions` (see `inboxQueries.myPermissions`). The backend already
 * scopes the conversation LIST by this same model, but the FE needs it locally
 * to:
 *   - render the "Mi equipo" tab (filter by `teamIds`),
 *   - decide, on a realtime assignment event, whether a conversation just
 *     entered or left the agent's view ({@link isConversationVisible}).
 *
 * Lives in a Zustand store (not just a query cache) so non-React code — the
 * realtime manager — can read it synchronously via `getState()`, the same
 * pattern as the presence + auth stores.
 */
interface PermissionsState {
  /** True once `/me/permissions` has resolved at least once this session. */
  loaded: boolean;
  /** The current user's id, as the backend reports it (authoritative). */
  userId: string | null;
  /** Base role is used ONLY for the channel-membership bypass. Action gates
   * continue to use the effective permissions map below. */
  baseRole: string | null;
  /** UUIDs of the teams the user is an active member of. */
  teamIds: string[];
  /** `customer_chat_view_all` — tenant-wide unless optional channel scoping
   * narrows a non-bypass user. */
  canViewAll: boolean;
  /** `customer_chat_forward` — may forward an inbox message into an internal
   *  team-chat channel. admin/supervisor/agent have it by default; the action
   *  ("Reenviar") is hidden when false. Mirrored here (not just read off the
   *  permissions bag in a component) so the gate is one cheap selector. */
  canForward: boolean;
  /**
   * The FULL effective-permissions map from `GET /me/permissions`
   * (`RolePermissions`, snake_case keys → boolean). This is the source of truth
   * for gating admin/destructive actions in the UI — NEVER gate off the JWT
   * `role` (breaks with custom roles and role changes). Read it via {@link useCan}.
   * `canViewAll`/`canForward` above are convenience mirrors of two hot keys the
   * realtime manager needs synchronously; everything else lives here.
   */
  permissions: Record<string, boolean>;
  hydrate: (p: {
    userId: string | null;
    baseRole: string | null;
    teamIds: string[];
    canViewAll: boolean;
    canForward: boolean;
    permissions: Record<string, boolean>;
  }) => void;
  clear: () => void;
}

export const usePermissionsStore = create<PermissionsState>((set) => ({
  loaded: false,
  userId: null,
  baseRole: null,
  teamIds: [],
  canViewAll: false,
  canForward: false,
  permissions: {},
  hydrate: ({ userId, baseRole, teamIds, canViewAll, canForward, permissions }) =>
    set({ loaded: true, userId, baseRole, teamIds, canViewAll, canForward, permissions }),
  clear: () =>
    set({
      loaded: false,
      userId: null,
      baseRole: null,
      teamIds: [],
      canViewAll: false,
      canForward: false,
      permissions: {},
    }),
}));

/** Reactive selector for the "Mi equipo" filter + count. */
export const useMyTeamIds = () => usePermissionsStore((s) => s.teamIds);

/** Reactive gate for broad Inbox visibility (possibly channel-scoped). */
export const useCanViewAll = () => usePermissionsStore((s) => s.canViewAll);

/** Reactive gate for the "Reenviar" (forward-to-channel) action. */
export const useCanForward = () => usePermissionsStore((s) => s.canForward);

/**
 * Reactive permission gate. `useCan('settings_manage_users')` → boolean.
 *
 * Returns `true` ONLY when the effective-permissions map explicitly has the key
 * set to `true` (a missing key — e.g. permissions not loaded yet — reads as
 * `false`, so the UI fails closed rather than flashing a forbidden action).
 * Use this to decide whether to render admin/destructive controls; mirror the
 * action→permission map in the backend (e.g. delete/role/invite user →
 * `settings_manage_users`).
 */
export const useCan = (key: string): boolean =>
  usePermissionsStore((s) => s.permissions[key] === true);

/** Non-reactive variant for imperative code (outside React render). */
export const can = (key: string): boolean =>
  usePermissionsStore.getState().permissions[key] === true;

/**
 * Is this conversation visible to the agent described by `perms`? Mirrors the
 * backend's scoping EXACTLY so the realtime upsert agrees with what a fresh
 * `GET /inbox/conversations` would return:
 *
 *   without view_all → assigned to me/my team only
 *   view_all + no channel scope (or admin bypass) → everything
 *   view_all + channel scope → accessible channel OR explicit assignment
 *
 * Since the backend's 2026-07-22 strict-assignment contract, an unassigned
 * conversation is NEVER visible without `customer_chat_view_all`, regardless
 * of team membership. Agents receive work by direct/team assignment.
 */
export function isConversationVisible(
  conv: {
    assigned_user_id?: string;
    assigned_team_id?: string;
    channel_id?: string;
  },
  perms: Pick<PermissionsState, 'userId' | 'teamIds' | 'canViewAll'> & {
    baseRole?: string | null;
    /** undefined means the module lookup has not resolved; fail closed. */
    channelScopingEnabled?: boolean;
    /** IDs returned by the already server-filtered GET /inbox/channels. */
    accessibleChannelIds?: readonly string[];
  },
): boolean {
  const assignedToUser =
    !!conv.assigned_user_id && conv.assigned_user_id === perms.userId;
  const assignedToTeam =
    !!conv.assigned_team_id && perms.teamIds.includes(conv.assigned_team_id);
  const explicitlyAssigned = assignedToUser || assignedToTeam;

  // Strict assignment remains the whole rule for users without view_all.
  if (!perms.canViewAll) return explicitlyAssigned;

  // Membership bypass is intentionally BASE-role based. Supervisors do not
  // bypass even if their custom effective permissions grant view_all.
  const bypass =
    perms.baseRole === 'admin' ||
    perms.baseRole === 'super_admin' ||
    perms.baseRole === 'super_admin_acting_as_tenant';
  if (bypass) return true;

  // Until /me/modules resolves, do not let a tenant-wide WS broadcast surface
  // a possibly out-of-scope conversation. A subsequent list refetch reconciles.
  if (perms.channelScopingEnabled === undefined) return explicitlyAssigned;
  if (!perms.channelScopingEnabled) return true;

  // In scoped tenants, an explicit assignment wins even across channels.
  if (explicitlyAssigned) return true;
  return (
    !!conv.channel_id &&
    Array.isArray(perms.accessibleChannelIds) &&
    perms.accessibleChannelIds.includes(conv.channel_id)
  );
}
